"use client";

import type { LessonDetails } from "@/features/workspaces/types/lessonDetails.ts";

import { motion } from "motion/react";
import { Icon } from "@iconify-icon/react";
import { useTranslation } from "react-i18next";

interface LessonActivityCardProps {
  lessonDetails: LessonDetails;
}

export const LessonActivityCard = ({ lessonDetails }: LessonActivityCardProps) => {
  // Placeholder data for activity
  console.log(lessonDetails);
  const activities = [
    {
      type: "reminder",
      title: "Lesson reminder",
      description: "Your lesson starts in 30 minutes",
      time: "Just now",
      icon: "mdi:bell",
      color: "blue",
    },
    {
      type: "material_shared",
      title: "Materials shared",
      description: "New materials added by tutor",
      time: "2 hours ago",
      icon: "mdi:file-upload",
      color: "green",
    },
    {
      type: "lesson_scheduled",
      title: "Lesson confirmed",
      description: "Your lesson has been confirmed",
      time: "1 day ago",
      icon: "mdi:calendar-check",
      color: "purple",
    },
  ];
  const { t } = useTranslation(["workspaces", "common"]);

  // Placeholder data for preparation checklist
  const preparationTasks = [
    { task: "Review materials", completed: true },
    { task: "Test microphone", completed: false },
    { task: "Prepare questions", completed: false },
  ];

  const getColorClasses = (color: string) => {
    switch (color) {
      case "blue":
        return "bg-blue-500/20 text-blue-400";
      case "green":
        return "bg-green-500/20 text-green-400";
      case "purple":
        return "bg-purple-500/20 text-purple-400";
      default:
        return "bg-muted/40 text-muted-foreground";
    }
  };

  return (
    <motion.div
      animate={{ opacity: 1, y: 0 }}
      className="col-span-12 lg:col-span-7"
      initial={{ opacity: 0, y: 20 }}
      transition={{ duration: 0.4, delay: 0.3 }}
    >
      <div className="bg-muted/20 text-accent-foreground rounded-2xl border-none p-4 shadow-2xl sm:p-6">
        <h3 className="mb-4 flex items-center gap-3 text-lg font-bold sm:mb-6 sm:text-xl">
          <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-orange-500/20 sm:h-10 sm:w-10">
            <Icon className="h-4 w-4 text-orange-400 sm:h-5 sm:w-5" icon="mdi:timeline-text" />
          </div>
          {t("workspaces:activity", "Activity")}
        </h3>

        <div className="mb-4 space-y-3 sm:mb-6 sm:space-y-4">
          {activities.map((activity, index) => (
            <motion.div
              key={index}
              animate={{ opacity: 1, x: 0 }}
              className="bg-muted/30 hover:bg-muted/40 rounded-xl p-3 backdrop-blur-sm transition-all sm:p-4"
              initial={{ opacity: 0, x: -10 }}
              transition={{ duration: 0.2, delay: index * 0.1 }}
            >
              <div className="flex gap-3 sm:gap-4">
                <div
                  className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl sm:h-12 sm:w-12 ${getColorClasses(activity.color)}`}
                >
                  <Icon className="h-4 w-4 sm:h-5 sm:w-5" icon={activity.icon} />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium sm:text-base">{activity.title}</p>
                  <p className="text-muted-foreground text-xs sm:text-sm">{activity.description}</p>
                  <p className="text-muted-foreground mt-1 text-xs sm:mt-2">{activity.time}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        <div className="bg-muted/30 rounded-xl p-3 backdrop-blur-sm sm:p-4">
          <h4 className="text-accent-foreground mb-3 flex items-center gap-2 text-sm font-medium sm:mb-4 sm:gap-3 sm:text-base">
            <div className="flex h-6 w-6 items-center justify-center rounded-lg bg-blue-500/20 sm:h-8 sm:w-8">
              <Icon className="h-3 w-3 text-blue-400 sm:h-4 sm:w-4" icon="mdi:clipboard-check" />
            </div>
            {t("workspaces:preparation", "Preparation")}
          </h4>
          <div className="space-y-2 sm:space-y-3">
            {preparationTasks.map((item, index) => (
              <div key={index} className="flex items-center gap-2 sm:gap-3">
                <div
                  className={`flex h-5 w-5 items-center justify-center rounded-lg sm:h-6 sm:w-6 ${item.completed ? "bg-green-500/20" : "bg-muted/40"}`}
                >
                  <input
                    checked={item.completed}
                    className="h-3 w-3 rounded border-none bg-transparent sm:h-4 sm:w-4"
                    type="checkbox"
                    onChange={() => {
                      /* Handle checkbox */
                    }}
                  />
                </div>
                <span
                  className={`text-xs sm:text-sm ${item.completed ? "text-muted-foreground line-through" : "text-accent-foreground"}`}
                >
                  {item.task}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </motion.div>
  );
};
